import React, { useEffect, useMemo, useState } from "react";

import Button from "../../../Components/Button";
import CommonModal from "../../../Components/Modal";
import Select from "../../../Components/Select";
import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import { fetchDormantHistory } from "../../../Store/Reducers/TeamViewsSlice";
import { getInitials } from "../../../Utils/Helper/Helper";
import { getVendorProfileAvatarBg } from "../../../Utils/Helper/profileAvatarUi";

const ACCOUNT_TEAM_ROLE_HINTS = new Set([
  "ae",
  "csm",
  "se",
  "tam",
  "bcs",
  "sdr",
  "vp",
  "account_executive",
  "customer_success_manager",
  "account_team",
]);

function normalizeRoleSlug(role) {
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function ownerIsAccountTeam(ownerRole) {
  const slug = normalizeRoleSlug(ownerRole);
  if (!slug) return true;
  if (ACCOUNT_TEAM_ROLE_HINTS.has(slug)) return true;
  if (
    slug.includes("sponsor") ||
    slug.includes("executive") ||
    slug === "c_level" ||
    slug === "c-level"
  ) {
    return false;
  }
  // Short role codes (AE, CSM, …) default to account-team blue.
  if (/^[a-z]{1,4}$/.test(slug)) return true;
  return true;
}

function recordStateKey(record) {
  if (record.superseded_by_id) return "transferred";
  if (record.dismissed_at) return "dismissed";
  return "open";
}

function recordStatusDisplay(record) {
  if (record.superseded_by_id) {
    return {
      text: record.successor_name
        ? `Transferred to ${record.successor_name}`
        : "Transferred",
      tone: "transferred",
    };
  }
  if (record.dismissed_at) {
    return { text: "Dismissed · history retained", tone: "dismissed" };
  }
  if (record.orphan_tier === 3) {
    return { text: "Orphaned · needs owner", tone: "orphaned" };
  }
  return { text: "Covered elsewhere", tone: "covered" };
}

function formatDormantDate(value) {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function recordMetaLine(record) {
  const parts = [formatDormantDate(record.dormant_at)];
  parts.push(
    record.latest_rl != null ? `RL ${record.latest_rl}` : "Never assessed"
  );
  if (record.goal_target_rl != null) {
    parts.push(`Goal RL ${record.goal_target_rl}`);
  } else if (record.has_goal) {
    parts.push("Goal set");
  }
  return parts.join(" · ");
}

export function CoverageHistoryModal({
  open,
  accountId,
  accountName,
  onClose,
  onViewRecord,
}) {
  const dispatch = useAppDispatch();
  const records =
    useAppSelector(
      state => state.teamViews.dormantHistoryByAccount[accountId]
    ) || [];
  const loading = useAppSelector(state => state.teamViews.historyLoading);
  const [stateFilter, setStateFilter] = useState("all");
  const [ownerFilter, setOwnerFilter] = useState("all");

  useEffect(() => {
    if (open && accountId) {
      dispatch(fetchDormantHistory(accountId));
      setStateFilter("all");
      setOwnerFilter("all");
    }
  }, [dispatch, open, accountId]);

  const ownerOptions = useMemo(() => {
    const byId = new Map();
    records.forEach(r => {
      if (!r.owner_user_id || byId.has(r.owner_user_id)) return;
      byId.set(r.owner_user_id, r.owner_name || "Prior owner");
    });
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [records]);

  const filteredRecords = useMemo(() => {
    return records.filter(record => {
      const state = recordStateKey(record);
      if (stateFilter !== "all" && state !== stateFilter) return false;
      if (
        ownerFilter !== "all" &&
        String(record.owner_user_id) !== ownerFilter
      ) {
        return false;
      }
      return true;
    });
  }, [records, stateFilter, ownerFilter]);

  const title = accountName
    ? `Dormant relationship history — ${accountName}`
    : "Dormant relationship history";

  const emptyMessage =
    records.length === 0
      ? "No dormant records yet."
      : "No dormant records match these filters.";

  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      size="xl"
      className="rl-prototype-skin coverage-history-modal"
      title={title}
      subtitle="Preserved relationships remain available after transfer or dismissal"
      footer={
        <Button variant="primary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="coverage-history-toolbar">
        <Select
          name="coverage-history-state"
          className="coverage-history-filter mb-0"
          value={stateFilter}
          placeholder=""
          isShowPlaceHolder={false}
          options={[
            { value: "all", label: `All records (${records.length})` },
            { value: "open", label: "Open / unresolved" },
            { value: "transferred", label: "Transferred" },
            { value: "dismissed", label: "Dismissed" },
          ]}
          onChange={e => setStateFilter(e.target.value || "all")}
        />
        <Select
          name="coverage-history-owner"
          className="coverage-history-filter coverage-history-filter--owner mb-0"
          value={ownerFilter}
          placeholder=""
          isShowPlaceHolder={false}
          options={[
            { value: "all", label: "All prior owners" },
            ...ownerOptions,
          ]}
          onChange={e => setOwnerFilter(e.target.value || "all")}
        />
      </div>

      {loading ? <p className="coverage-history-empty">Loading…</p> : null}

      {!loading && filteredRecords.length === 0 ? (
        <p className="coverage-history-empty">{emptyMessage}</p>
      ) : null}

      {!loading && filteredRecords.length > 0 ? (
        <ul className="coverage-history-list">
          {filteredRecords.map(record => {
            const status = recordStatusDisplay(record);
            const ownerName = record.owner_name || "Prior owner";
            const avatarBg = getVendorProfileAvatarBg(
              ownerIsAccountTeam(record.owner_role)
            );
            return (
              <li
                key={record.relationship_id}
                className="coverage-history-item"
              >
                <div
                  className="coverage-history-avatar"
                  style={{ background: avatarBg }}
                  aria-hidden
                >
                  {getInitials(ownerName)}
                </div>
                <div className="coverage-history-copy">
                  <div className="coverage-history-pair">
                    <strong>{ownerName}</strong>
                    <span className="coverage-history-arrow">→</span>
                    <span>{record.contact_name || "Contact"}</span>
                  </div>
                  <div className="coverage-history-meta-line">
                    {recordMetaLine(record)}
                  </div>
                </div>
                <div
                  className={`coverage-history-status coverage-history-status--${status.tone}`}
                >
                  {status.text}
                </div>
                <Button
                  variant="outline-primary"
                  className="coverage-history-view-btn"
                  onClick={() => onViewRecord?.(record)}
                >
                  View record
                </Button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </CommonModal>
  );
}

export default CoverageHistoryModal;
